import { Field, useField, useForm } from 'react-final-form';
import {
  ApplicationContextType,
  GstAnalysisStatus,
  UnderwritingFormStatus,
  validationErrorTypes,
} from '../../../../interfaces/interfaces';
import styles from './GstAccount.module.scss';
import OutlinedFormInput from '../../../Finance/components/OutlinedFormInput/OutlinedFormInput';
import MeritoButton from '../../../../components/common/MeritoButtons/MeritoButtons';
import { useContext, useEffect, useMemo, useState } from 'react';
import { FaCircleExclamation as Exclamation } from 'react-icons/fa6';
import { gstNumberValidation } from '../../../../components/functions/formValidation';
import { getIn } from 'final-form';
import { RiErrorWarningFill as ErrorIcon } from 'react-icons/ri';
import Switch from 'react-switch';
import PdfPasswordProtection from '../../components/MultiFileUploadModal/PdfPasswordProtection';

interface GstAccountDetailProps {
  fieldName: string;
  gstAccountIndex: number;
  formCompleteStatus: { [x: string]: UnderwritingFormStatus };
  gstApiErrors?: any;
  setGstApiErrors: React.Dispatch<React.SetStateAction<any>>;
  formContext?: any;
  formType: ApplicationContextType;
  requiresCertPassword?: boolean;
  certificateFileDetails?: any;
  onSubmitCertPassword?: (password: string) => void;
}

export default function GstAccountDetail({
  fieldName,
  gstAccountIndex,
  formCompleteStatus,
  gstApiErrors,
  setGstApiErrors,
  formContext,
  formType,
  requiresCertPassword,
  certificateFileDetails,
  onSubmitCertPassword,
}: GstAccountDetailProps) {
  const { input: { value: gstinValue } } = useField(`${fieldName}.gstin`, { subscription: { value: true } });
  const { input: { value: gstUserName } } = useField(`${fieldName}.username`, { subscription: { value: true } });
  const { input: { value: statementJobId } } = useField(`${fieldName}.statementJobId`, { subscription: { value: true } });
  const { input: { value: isVerified } } = useField(`${fieldName}.isVerified`, { subscription: { value: true } });
  const { input: { value: isInActiveValue } } = useField(`${fieldName}.isInActive`, { subscription: { value: true } });

  const { extraFormDetails, gstAnalysisJobs, fetchGstAnalysisJobs } = useContext(formContext) as any;
  const [currentAnalysisJob, setCurrentAnalysisJob] = useState(null);
  const [isInActive, setIsInActive] = useState(false);
  const [inActiveReason, setInActiveReason] = useState('');
  const [isInActiveModalOpen, setIsInActiveModalOpen] = useState(false);
  const { change } = useForm();

  useEffect(() => {
    if (isInActiveValue) {
      change(`${fieldName}.isInActive`, isInActiveValue);
    }
  }, [isInActiveValue]);

  // The row-list status badge (GstDetails.tsx) already shows a "Fetching
  // data" indicator while this job runs — no need to duplicate that here.
  // This effect just tracks the job for the error box below and flips
  // isVerified once the job actually succeeds.
  useEffect(() => {
    const currentJob = getIn(gstAnalysisJobs, `${statementJobId}`);
    if (currentJob) {
      setCurrentAnalysisJob(currentJob);
      if (currentJob?.status == GstAnalysisStatus.success) {
        if (!isVerified) {
          change(`${fieldName}.isVerified`, true);
        }
      }
    }
  }, [statementJobId, gstAnalysisJobs, isVerified]);

  const showAnalysisJobError = useMemo(() => {
    if (currentAnalysisJob?.status == GstAnalysisStatus.failure) {
      const errorDetails = currentAnalysisJob?.errorDetails;
      if (errorDetails) {
        return {
          title: 'GST Verification Failed',
          message: (
            <>
              {currentAnalysisJob?.errorDetails?.errorMessage}
              <br />
              Contact Merito
            </>
          ),
        };
      } else {
        return { title: 'GST Verification Failed', message: <>Contact Merito</> };
      }
    }
  }, [currentAnalysisJob]);

  return (
    <div className={styles.fieldItemContainer}>
      {showAnalysisJobError ? (
        <div className={styles.errorRootContainer}>
          <div className={styles.topContainer}>
            <div className={styles.titleContainer}>
              <div className={styles.iconContainer}>
                <ErrorIcon />
              </div>
              <div className={styles.title}>{showAnalysisJobError?.title}</div>
            </div>
            <div className={styles.subTitle}>{showAnalysisJobError?.message}</div>
          </div>
        </div>
      ) : null}

      {requiresCertPassword ? (
        <PdfPasswordProtection
          compact
          fileDetails={certificateFileDetails}
          showPasswordSuccess={false}
          handleSubmitPassword={(password) => onSubmitCertPassword?.(password)}
        />
      ) : null}

      <div className={styles.fieldContainer}>
        {/* Hidden form fields — GSTIN/username are shown in the list row,
            but must stay registered in react-final-form for validation + submit */}
        <Field
          validate={(value) => {
            const errors = gstNumberValidation(value);
            if (errors == validationErrorTypes.invalid) return 'Invalid GSTIN';
            if (errors == validationErrorTypes.required) return 'Enter GSTIN';
            return errors;
          }}
          name={`${fieldName}.gstin`}
        >
          {() => null}
        </Field>

        <Field
          validate={(value) => (value ? '' : 'Enter username')}
          name={`${fieldName}.username`}
        >
          {() => null}
        </Field>

        {/*{formType == ApplicationContextType.monthlyReport ? (
          <div className={styles.gstSwitchRootContainer}>
            <div className={styles.leftContainer}>
              <div className={styles.title}>GSTIN is active?</div>
              <div className={styles.subTitle}>Turn off if not used for filings.</div>
            </div>
            <div className={styles.rightContainer}>
              <Field name={`${fieldName}.isInActive`}>
                {(props) => (
                  <Switch
                    draggable={false}
                    checked={!isInActive}
                    onChange={(checked) => {
                      if (!checked) {
                        setIsInActive(true);
                        setInActiveReason('');
                        setIsInActiveModalOpen(true);
                      } else {
                        props.input.onChange(checked);
                      }
                    }}
                    onColor="#100828"
                    offColor="#D5D5D5"
                    handleDiameter={20}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    boxShadow="0px 1px 3px rgba(0, 0, 0, 0.6)"
                    activeBoxShadow="0px 0px 1px 1px rgba(0, 0, 0, 0.2)"
                    height={24}
                    width={43}
                    className={styles.switch}
                    id="material-switch"
                  />
                )}
              </Field>
            </div>
          </div>
        ) : null}*/}
      </div>

      {isInActiveModalOpen && (
        <div className={styles.inActiveModalOverlay}>
          <div className={styles.confirmationContainer}>
            <div className={styles.topContainer}>
              <div className={styles.iconContainer}>
                <Exclamation />
              </div>
              <div className={styles.title}>Not using this GSTIN anymore?</div>
              <div className={styles.subTitle}>
                <div className={styles.gstinValue}>{gstinValue}</div>
              </div>
            </div>
            <div className={styles.fieldContainer}>
              <div className={styles.label}>Reason</div>
              <OutlinedFormInput
                fieldType="lg"
                placeholder="GST cancelled, wrong registration, not in use"
                name="inactiveReason"
                value={inActiveReason}
                onChange={(e) => setInActiveReason(e.target.value)}
                error={null}
                touched={false}
              />
            </div>
            <div className={styles.bottomContainer}>
              <MeritoButton
                onClick={() => {
                  setIsInActive(false);
                  setInActiveReason('');
                  setIsInActiveModalOpen(false);
                }}
                size="lg"
                isRounded={true}
                btnType="button"
                type="secondary-2"
                btnText="Cancel"
              />
              <MeritoButton
                size="lg"
                disabled={!inActiveReason}
                onClick={() => {
                  if (!inActiveReason) return;
                  change(`${fieldName}.isInActive`, true);
                  change(`${fieldName}.inactiveReason`, inActiveReason);
                  setIsInActiveModalOpen(false);
                }}
                isRounded={true}
                btnType="button"
                type="primary-1"
                btnText="Stop Using GSTIN"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}